import { motion } from 'framer-motion'
import { 
  CreditCard, 
  Users, 
  BarChart3, 
  MessageSquare, 
  Trophy, 
  Calendar,
  Shield,
  Zap,
  Smartphone,
  Monitor,
  Tablet
} from 'lucide-react'

const Solutions = () => {
  const solutions = [
    {
      category: 'Gestão Financeira',
      icon: CreditCard,
      color: 'from-green-500 to-emerald-500',
      features: [
        'Automatização de recebimentos',
        'Controle de mensalidades',
        'Relatórios financeiros detalhados',
        'Baixa manual de pagamentos',
        'Previsão de recebimentos',
        'Indicadores de inadimplência'
      ]
    },
    {
      category: 'Gestão de Alunos',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      features: [
        'Cadastro completo de alunos',
        'Organização de turmas',
        'Controle de frequência',
        'Histórico de progresso',
        'Gestão de responsáveis',
        'Comunicação direta'
      ]
    },
    {
      category: 'Avaliações e Progresso',
      icon: Trophy,
      color: 'from-yellow-500 to-orange-500',
      features: [
        'Sistema de níveis personalizados',
        'Avaliações periódicas',
        'Troféus e conquistas',
        'Acompanhamento de requisitos',
        'Relatórios de desempenho',
        'Gamificação'
      ]
    },
    {
      category: 'Comunicação',
      icon: MessageSquare,
      color: 'from-purple-500 to-pink-500',
      features: [
        'Chat interno integrado',
        'Notificações push',
        'Mensagens em massa',
        'Comunicação por turma',
        'Histórico de conversas',
        'Suporte direto'
      ]
    },
    {
      category: 'Agenda e Organização',
      icon: Calendar,
      color: 'from-red-500 to-pink-500',
      features: [
        'Agenda diária e semanal',
        'Controle de horários',
        'Gestão de professores',
        'Organização de eventos',
        'Lembretes automáticos',
        'Sincronização de calendários'
      ]
    },
    {
      category: 'Relatórios e Analytics',
      icon: BarChart3,
      color: 'from-indigo-500 to-purple-500',
      features: [
        'Dashboards personalizados',
        'KPIs em tempo real',
        'Relatórios exportáveis',
        'Análise de tendências',
        'Métricas de performance',
        'Indicadores de gestão'
      ]
    }
  ]

  const apps = [
    {
      name: 'App do Aluno',
      icon: Smartphone,
      color: 'from-blue-500 to-cyan-500',
      description: 'Acompanhe seu progresso, veja avaliações e gerencie pagamentos.',
      benefits: [
        'Visualização do nível atual',
        'Acompanhamento de troféus',
        'Gestão de mensalidades',
        'Avaliações dos professores',
        'Comunicação direta'
      ]
    },
    {
      name: 'App da Escola',
      icon: Monitor,
      color: 'from-green-500 to-emerald-500',
      description: 'Controle total da sua escola com todas as ferramentas de gestão.',
      benefits: [
        'Gestão completa de alunos',
        'Controle financeiro',
        'Relatórios detalhados',
        'Agenda organizacional',
        'Sistema de mensagens'
      ]
    },
    {
      name: 'App do Professor',
      icon: Tablet,
      color: 'from-purple-500 to-pink-500',
      description: 'Faça avaliações e gerencie sua agenda de trabalho.',
      benefits: [
        'Avaliações dos alunos',
        'Agenda de trabalho',
        'Comunicação com alunos',
        'Visualização de turmas',
        'Relatórios de desempenho'
      ]
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="gradient-bg py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl lg:text-6xl font-bold text-dark-900 mb-6">
              Nossas Soluções
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
              Descubra como nossa solução pode transformar sua escola de natação 
              com funcionalidades completas e especializadas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-dark-900 mb-4">
              Funcionalidades Principais
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nossa plataforma oferece todas as ferramentas necessárias para 
              modernizar e otimizar a gestão da sua escola de natação.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="card h-full hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${solution.color} text-white mb-6`}>
                    <solution.icon className="h-8 w-8" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-dark-900 mb-4">
                    {solution.category}
                  </h3>
                  
                  <ul className="space-y-3">
                    {solution.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${solution.color} mt-2 flex-shrink-0`}></div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Apps Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-dark-900 mb-4">
              Nossos Apps Especializados
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada app foi desenvolvido especificamente para atender às necessidades 
              únicas de cada perfil de usuário.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {apps.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group"
              >
                <div className={`bg-white rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2`}>
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${app.color} text-white mb-6`}>
                    <app.icon className="h-8 w-8" />
                  </div>

                  <h3 className="text-2xl font-bold text-dark-900 mb-3">
                    {app.name}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {app.description}
                  </p>

                  <ul className="space-y-3">
                    {app.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${app.color} mt-2 flex-shrink-0`}></div>
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Pronto para transformar sua escola?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Descubra como nossa solução pode ajudar sua escola de natação 
              a crescer e se modernizar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contato" className="btn-primary bg-white text-primary-600 hover:bg-gray-100">
                Solicitar Demo Gratuito
              </a>
              <a href="/apps" className="btn-outline border-white text-white hover:bg-white hover:text-primary-600">
                Conhecer os Apps
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Solutions
